import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Hexagon, Users, Clock, CheckSquare, Settings,
  BarChart3, LogOut, Menu, X, Plus, ArrowLeft, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import DateRangeField from '../components/DateRangeField';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { leaveApi } from '../lib/api';
import { toast } from 'sonner';
import BrandedToaster from '../components/BrandedToaster';
import { format } from 'date-fns';
import { LEAVE_REASON_HINT } from '../lib/leaveReasonHint';
import { bookedMessage } from '../lib/bookingOutcome';

const NewLeavePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [formData, setFormData] = useState({
    leave_type_id: '',
    start_date: null,
    end_date: null,
    start_type: 'morning',
    end_type: 'end_of_day',
    reason: ''
  });

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  const fetchLeaveTypes = async () => {
    try {
      const response = await leaveApi.getTypes();
      setLeaveTypes(response.data);
      if (response.data.length > 0) {
        setFormData(prev => ({ ...prev, leave_type_id: response.data[0].id }));
      }
    } catch (error) {
      console.error('Failed to fetch leave types:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.start_date || !formData.end_date) {
      toast.error('Please select start and end dates');
      return;
    }

    setLoading(true);
    try {
      const res = await leaveApi.createRequest({
        ...formData,
        start_date: format(formData.start_date, 'yyyy-MM-dd'),
        end_date: format(formData.end_date, 'yyyy-MM-dd')
      });
      // Trimmed because they were already off for part of the range — same
      // reasoning as RequestLeaveDialog: never change somebody's dates silently.
      if (res?.data?.overlap_note) {
        // Stays until dismissed — see RequestLeaveDialog for why.
        toast.warning(res.data.overlap_note, { duration: Infinity, closeButton: true });
      } else if (res?.data?.auto_approved) {
        // H9: it approved itself — same words as the booking dialog.
        toast.success(bookedMessage(res.data), {
          description: "It was within the rules, so it didn't need to wait for anyone.",
        });
      } else {
        toast.success('Leave request submitted!');
      }
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit request';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background flex">
      <BrandedToaster />
      
      {/* Shared Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-sm border-b border-border">
          <div className="flex items-center gap-4 px-6 py-4">
            <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <Link to="/dashboard" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-heading text-2xl font-bold">Request leave</h1>
          </div>
        </header>

        <div className="p-6 max-w-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="card-hive p-6 space-y-6">
              {/* Leave Type */}
              <div className="space-y-2">
                <Label>Leave type</Label>
                <Select 
                  value={formData.leave_type_id} 
                  onValueChange={(value) => setFormData({ ...formData, leave_type_id: value })}
                >
                  <SelectTrigger className="input-hive" data-testid="leave-type-select">
                    <SelectValue placeholder="Select leave type" />
                  </SelectTrigger>
                  <SelectContent>
                    {leaveTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: type.color }} />
                          {type.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date Range — ONE pop-up for both days (DateRangeField, Rob 3A 02/11/2026) */}
              <DateRangeField
                start={formData.start_date}
                end={formData.end_date}
                onChange={({ start, end }) => setFormData({ ...formData, start_date: start, end_date: end })}
                startTestId="start-date-picker"
                endTestId="end-date-picker"
              />

              {/* Start/End Type */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start</Label>
                  <Select 
                    value={formData.start_type} 
                    onValueChange={(value) => setFormData({ ...formData, start_type: value })}
                  >
                    <SelectTrigger className="input-hive">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">Morning</SelectItem>
                      <SelectItem value="afternoon">Afternoon</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>End</Label>
                  <Select 
                    value={formData.end_type} 
                    onValueChange={(value) => setFormData({ ...formData, end_type: value })}
                  >
                    <SelectTrigger className="input-hive">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lunchtime">Lunchtime</SelectItem>
                      <SelectItem value="end_of_day">End of day</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Reason */}
              <div className="space-y-2">
                <Label>Reason (optional)</Label>
                <Textarea
                  placeholder="Add any notes for your manager..."
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="input-hive min-h-[100px] resize-none"
                  data-testid="leave-reason-input"
                />
                <p className="text-xs text-muted-foreground" data-testid="leave-reason-hint">{LEAVE_REASON_HINT}</p>
              </div>
            </div>

            {/* Submit */}
            <div className="flex gap-4">
              <Button
                type="button"
                variant="secondary"
                className="btn-secondary"
                onClick={() => navigate('/dashboard')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn-primary flex-1"
                disabled={loading}
                data-testid="submit-leave-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Submit request
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default NewLeavePage;
